import { hasAtLeast, hasRole, useAuthStore } from '@insightai/auth'
import type { Role } from '@insightai/api-client'
import { useParams } from 'react-router-dom'

/**
 * F0-47 — RBAC and workspace-scope hooks.
 *
 * AC-53 boundary, restated because it is the single easiest thing to get wrong:
 * these hooks decide what to RENDER. They never decide what is ALLOWED. The
 * server enforces authorisation and returns 403; hiding a button is UX.
 * A mutation guarded only by `can()` is a security bug, not a UX shortcut.
 */
export function useRBAC() {
  const roles = useAuthStore((state) => state.user?.roles)

  return {
    roles: roles ?? [],
    is: (role: Role) => hasRole(roles, role),
    atLeast: (minimum: Role) => hasAtLeast(roles, minimum),
    /** Methodology/ops surfaces appear for RESEARCH_LEAD and above (§6.1). */
    isMethodologyUser: hasAtLeast(roles, 'RESEARCH_LEAD'),
    isResearchOps: hasRole(roles, 'RESEARCH_OPS') || hasAtLeast(roles, 'WORKSPACE_ADMIN'),
    isPlatformAdmin: hasRole(roles, 'PLATFORM_ADMIN'),
  }
}

/**
 * The active workspace id, preferring the URL over stored state.
 *
 * URL-first matters: a deep link shared between colleagues must open the
 * workspace in the link, not whichever one the recipient last visited.
 */
export function useWorkspaceScope() {
  const { wsId } = useParams<{ wsId: string }>()
  const storeWorkspaceId = useAuthStore((state) => state.activeWorkspaceId)
  const workspaces = useAuthStore((state) => state.user?.workspaces ?? [])
  const activeWorkspaceId = wsId ?? storeWorkspaceId ?? null

  return {
    workspaceId: activeWorkspaceId,
    workspace: workspaces.find((w) => w.id === activeWorkspaceId) ?? null,
    workspaces,
    /** True when the URL names a workspace the user has no membership in. */
    isOutOfScope: Boolean(wsId) && workspaces.length > 0 && !workspaces.some((w) => w.id === wsId),
  }
}
